import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { LogoOption1, LogoOption2, LogoOption3, LogoOption4, LogoOption5 } from '../components/LogoAlternatives';
import { Logo } from '../components/Logo';

export const LogoPreviewPage: React.FC = () => {
  const logos = [
    { 
      name: 'Current Logo', 
      component: Logo, 
      description: 'Medical shield with stethoscope and cross'
    },
    { 
      name: 'Option 1: Document Timeline', 
      component: LogoOption1, 
      description: 'Modern document with timeline dots - emphasizes chronology'
    },
    { 
      name: 'Option 2: CC Monogram', 
      component: LogoOption2, 
      description: 'Minimalist CC letters with medical cross accent'
    },
    { 
      name: 'Option 3: Medical Pulse', 
      component: LogoOption3, 
      description: 'Abstract timeline with ECG/pulse line - dynamic and modern'
    },
    { 
      name: 'Option 4: Chronicle Book', 
      component: LogoOption4, 
      description: 'Book/journal with medical cross - emphasizes documentation'
    },
    { 
      name: 'Option 5: Hexagon Timeline', 
      component: LogoOption5, 
      description: 'Professional hexagon badge with vertical timeline'
    },
  ];


  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50/30 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <Link 
          to="/" 
          className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 mb-8 font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Homepage</span>
        </Link>

        <div className="mb-12">
          <h1 className="text-4xl font-bold text-medical-900 mb-4">Logo Options</h1>
          <p className="text-xl text-medical-600">
            Choose the logo that best represents ClinLexis
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {logos.map((logo, index) => {
            const LogoComponent = logo.component;
            return (
              <div 
                key={index}
                className="bg-white rounded-2xl border border-medical-200 p-8 hover:shadow-xl transition-shadow"
              >
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-medical-900 mb-2">
                    {logo.name}
                  </h3>
                  <p className="text-sm text-medical-600">
                    {logo.description}
                  </p>
                </div>

                {/* Large preview */}
                <div className="bg-medical-50 rounded-xl p-8 mb-6 flex items-center justify-center">
                  <LogoComponent size="xl" showText={false} />
                </div>

                {/* With text preview */}
                <div className="bg-white border border-medical-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-center">
                    <LogoComponent size="md" showText={true} />
                  </div>
                </div>

                {/* Small preview (header size) */}
                <div className="bg-medical-900 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <LogoComponent size="md" showText={true} className="[&_span]:text-white [&_span:last-child]:text-primary-300" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 bg-primary-50 border border-primary-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-medical-900 mb-2">
            How to apply your choice
          </h3>
          <p className="text-medical-700">
            Once you've decided, let me know which option you prefer (e.g., "I like Option 3"), 
            and I'll update the Logo component to use that design throughout the application.
          </p>
        </div>
      </div>
    </div>
  );
};
